/*
 * Unlimited* storefront glue for Fin (Intercom).
 *
 * 1. "Talk to us" buttons: any element with [data-ul-fin] opens the Intercom Messenger
 *    with the message in data-ul-fin pre-filled. Without the Messenger loaded, the
 *    element's href (default /pages/contact) is followed.
 * 2. Cart sync: when Fin changes the cart it dispatches `intercom:cartUpdated`.
 *    This re-dispatches Shopify's standard `shopify:cart:lines-update` event with the
 *    fresh cart and drawer section HTML, so Horizon's cart icon and drawer refresh in place.
 */

(() => {
  const LINES_UPDATE = 'shopify:cart:lines-update';

  /* ---------- Talk to us ---------- */

  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-ul-fin]') : null;
    if (!trigger || typeof window.Intercom !== 'function') return;

    event.preventDefault();
    const message = trigger.getAttribute('data-ul-fin') || '';
    if (message) {
      window.Intercom('showNewMessage', message);
    } else {
      window.Intercom('show');
    }
  });

  /* ---------- Cart sync ---------- */

  let pending = null;

  const cartSectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((el) => el.getAttribute('data-section-id'))
      .filter(Boolean);

  async function refreshCart() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

    const ids = [...new Set(cartSectionIds())];
    let sections = {};
    if (ids.length) {
      const url = `${window.location.pathname}?sections=${encodeURIComponent(ids.join(','))}`;
      sections = await fetch(url, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : {}))
        .catch(() => ({}));
    }

    return {
      cart: {
        id: cart.token,
        totalQuantity: cart.item_count,
        cost: { totalAmount: { amount: String(cart.total_price / 100), currencyCode: cart.currency } },
        lines: cart.items.map((item) => ({
          id: item.key,
          quantity: item.quantity,
          cost: { totalAmount: { amount: String(item.final_line_price / 100), currencyCode: cart.currency } },
        })),
        discountCodes: [],
      },
      detail: { sections, itemCount: cart.item_count, source: 'intercom' },
    };
  }

  const dispatchLinesUpdate = (promise) => {
    const event = new Event(LINES_UPDATE, { bubbles: true });
    Object.assign(event, { action: 'add', context: 'standard-action', lines: [], promise });
    document.dispatchEvent(event);
  };

  /**
   * Horizon's cart components register after page load. An update dispatched before they
   * listen is missed, which leaves the drawer showing "empty". Re-dispatch once they are defined.
   */
  async function ensureDrawerCaughtUp(result) {
    if (!result || result.cart.totalQuantity === 0) return;
    if (!document.querySelector('#cart-drawer [data-cart-drawer-empty]')) return;
    if (window.customElements) await customElements.whenDefined('cart-items-component');
    if (document.querySelector('#cart-drawer [data-cart-drawer-empty]')) dispatchLinesUpdate(refreshCart());
  }

  /** Refreshes Horizon's cart icon and drawer, then opens the drawer when the cart has items. */
  function syncCart() {
    if (pending) return pending;

    const promise = refreshCart();
    pending = promise;
    dispatchLinesUpdate(promise);

    return promise
      .then(async (result) => {
        await ensureDrawerCaughtUp(result);
        if (result.cart.totalQuantity > 0) {
          const drawer = document.getElementById('cart-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();
        }
        return result;
      })
      .catch((error) => console.warn('[unlimited-fin] cart refresh failed', error))
      .finally(() => {
        pending = null;
      });
  }

  function onFinCartUpdated() {
    syncCart();
  }

  /* ---------- Add to cart for Unlimited* sections ---------- */

  /** Adds a variant with the Cart AJAX API, then syncs the drawer. Rejects with Shopify's message. */
  async function addToCart(variantId, quantity = 1) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Could not add to cart');
    await syncCart();
    return data;
  }

  window.ULCart = { add: addToCart, sync: syncCart };

  document.addEventListener('submit', async (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (!form || !form.hasAttribute('data-ul-add')) return;
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    const status = form.querySelector('[data-ul-add-status]');
    const id = form.querySelector('[name="id"]')?.value;
    if (!id) return;

    if (button) button.disabled = true;
    if (status) status.textContent = '';
    try {
      await addToCart(id);
    } catch (error) {
      if (status) status.textContent = error.message;
    } finally {
      if (button) button.disabled = false;
    }
  });

  window.addEventListener('intercom:cartUpdated', onFinCartUpdated);
  document.addEventListener('intercom:cartUpdated', (event) => {
    if (event.target === document) onFinCartUpdated();
  });
})();
